(() => {
  const initialiseGallery = (gallery) => {
    if (gallery.dataset.ecGalleryReady === 'true') return;

    const slides = Array.from(gallery.querySelectorAll('[data-ec-rv7-slide]'));
    const thumbnails = Array.from(gallery.querySelectorAll('[data-ec-rv7-thumbnail]'));
    const previous = gallery.querySelector('[data-ec-rv7-previous]');
    const next = gallery.querySelector('[data-ec-rv7-next]');
    const current = gallery.querySelector('[data-ec-rv7-current]');
    const total = gallery.querySelector('[data-ec-rv7-total]');
    const caption = gallery.querySelector('[data-ec-rv7-caption]');

    if (!slides.length) return;

    let activeIndex = 0;

    const show = (requestedIndex, moveFocus = false) => {
      activeIndex = (requestedIndex + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      thumbnails.forEach((thumbnail, index) => {
        const isActive = index === activeIndex;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
        if (isActive && moveFocus) thumbnail.focus({ preventScroll: true });
      });

      if (current) current.textContent = String(activeIndex + 1);
      if (total) total.textContent = String(slides.length);
      if (caption) caption.textContent = slides[activeIndex].dataset.caption || '';
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => show(index));
    });

    previous?.addEventListener('click', () => show(activeIndex - 1));
    next?.addEventListener('click', () => show(activeIndex + 1));

    gallery.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        show(activeIndex - 1, true);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        show(activeIndex + 1, true);
      }
    });

    gallery.addEventListener('ec:show-media-block', (event) => {
      const targetIndex = slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
      if (targetIndex >= 0) show(targetIndex);
    });

    gallery.dataset.ecGalleryReady = 'true';
    show(0);
  };

  const initialiseAll = (root = document) => {
    root.querySelectorAll('[data-ec-rv7-gallery]').forEach(initialiseGallery);
  };

  const readRechargeSelection = (widget) => {
    let purchaseMode = null;
    let sellingPlanId = null;

    const inspectRoot = (root) => {
      root.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!input.checked) return;
        if (input.name === 'purchaseOption') purchaseMode = input.value;
        if (input.name === 'rc_plan') sellingPlanId = input.value;
      });
      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) inspectRoot(element.shadowRoot);
      });
    };

    if (widget?.shadowRoot) inspectRoot(widget.shadowRoot);
    return { purchaseMode, sellingPlanId };
  };

  const formatMoney = (cents, currency) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD'
  }).format(Number(cents || 0) / 100);

  const cadenceLabel = (planName) => {
    const match = String(planName || '').match(/(\d+)\s*[- ]?month/i);
    if (!match) return 'at the selected delivery interval';
    const count = Number(match[1]);
    return `every ${count} ${count === 1 ? 'month' : 'months'}`;
  };

  const initialiseSubscriptionTruth = (section) => {
    if (section.dataset.ecSubscriptionTruthReady === 'true') return;

    const dataElement = section.querySelector('[data-ec-rv7-plan-data]');
    const summary = section.querySelector('[data-ec-rv7-subscription-truth]');
    const today = section.querySelector('[data-ec-rv7-subscription-today]');
    const next = section.querySelector('[data-ec-rv7-subscription-next]');
    const widget = section.querySelector('recharge-subscription-widget');
    if (!dataElement || !summary || !today || !next || !widget) return;

    // Recharge upgrades its custom element asynchronously. Wait for its shadow
    // root before reading the selected purchase mode or delivery plan.
    if (!widget.shadowRoot) {
      let attempts = 0;
      const waitForRecharge = window.setInterval(() => {
        attempts += 1;
        if (widget.shadowRoot || attempts >= 80) {
          window.clearInterval(waitForRecharge);
          if (widget.shadowRoot) initialiseSubscriptionTruth(section);
        }
      }, 100);
      return;
    }

    let planData;
    try {
      planData = JSON.parse(dataElement.textContent || '{}');
    } catch (_) {
      return;
    }
    if (!Array.isArray(planData.plans) || !planData.plans.length) return;

    const refresh = () => {
      const selection = readRechargeSelection(widget);
      if (selection.purchaseMode === 'onetime') {
        summary.hidden = true;
        return;
      }

      const selectedPlan = planData.plans.find((plan) => String(plan.id) === String(selection.sellingPlanId)) || planData.plans[0];
      today.textContent = `${formatMoney(selectedPlan.first_price, planData.currency)} today`;
      next.textContent = `Then ${formatMoney(selectedPlan.recurring_price, planData.currency)} ${cadenceLabel(selectedPlan.cadence)}.`;
      summary.hidden = false;
    };

    const observeRoot = (root) => {
      if (!root || root.__ecSubscriptionTruthObserved) return;
      root.__ecSubscriptionTruthObserved = true;
      root.addEventListener('change', refresh);
      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) observeRoot(element.shadowRoot);
      });
    };

    observeRoot(widget.shadowRoot);
    const widgetObserver = new MutationObserver(() => {
      observeRoot(widget.shadowRoot);
      refresh();
    });
    widgetObserver.observe(widget.shadowRoot, { childList: true, subtree: true });

    section.dataset.ecSubscriptionTruthReady = 'true';
    refresh();
  };

  const initialiseSubscriptionTruthAll = (root = document) => {
    root.querySelectorAll('[data-ec-rv7-section]').forEach(initialiseSubscriptionTruth);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseAll();
      initialiseSubscriptionTruthAll();
    });
  } else {
    initialiseAll();
    initialiseSubscriptionTruthAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAll(event.target);
    initialiseSubscriptionTruthAll(event.target);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('[data-ec-rv7-section]');
    const gallery = section?.querySelector('[data-ec-rv7-gallery]');
    if (!gallery) return;
    gallery.dispatchEvent(new CustomEvent('ec:show-media-block', {
      detail: { blockId: event.detail.blockId }
    }));
  });
})();
